import { useState, useEffect, useMemo } from "react";
import SearchSelect from "./SearchSelect";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import { downloadXlsx } from "./xlsxExport";
import { loadFromSupabase, saveToSupabase, saveManyToSupabase, deleteFromSupabase } from "../supabaseDB";
import { PaymentConfirmationPreview, partyAndRowsForInvoiceDeposit } from "./paymentConfirmation";
import { BatchDepositModal } from "./SweetLeafPortal";

/* SL Payments — read-only-by-default registry of payments recorded
   FROM WITHIN SweetLeafPortal.js (source === 'sl' in the shared
   `payments` table). Deliberately excludes GF-originated Four Seasons
   payments even though they land in the same table — see
   fix-dashboard.md's "REGLA DE FILTRO ESTRICTA".

   Own file (not inline in SweetLeafPortal.js, already ~7500 lines) per
   the *Registry.js convention in CLAUDE.md. Reuses BatchDepositModal
   (exported from SweetLeafPortal.js) for editing via its `existingPayment`
   prop, and paymentConfirmation.js's shared PDF builder (now accepting
   `opts.invoices` so it resolves SL invoices instead of defaulting to
   GF's "gfi-invoices").

   Create is NOT handled here — new SL payments are still recorded from
   the Invoices tab's own Batch Deposit flow (SweetLeafPortal.js's
   handleBatchDepositConfirm). This registry only lists / edits / deletes
   what's already been recorded, per the spec (no "+ New Payment" was
   asked for). */

var T = {
  en: {
    title: "SL Payments", subtitle: "Payments recorded from inside the SweetLeaf module.",
    payId: "PAY-ID", paymentDate: "Payment Date", client: "Client", invoicesApplied: "Invoice(s) Applied",
    amount: "Amount", method: "Method", reference: "Reference", status: "Status", actions: "Actions",
    statusApplied: "Applied", statusPending: "Pending Allocation",
    cardTotalDeposits: "Total Deposits (active season)", cardThisMonth: "Deposits This Month", cardPendingAlloc: "Pending Allocation",
    search: "Search PAY-ID, reference, invoice…", from: "From", to: "To",
    filterInvoice: "Invoice", allInvoices: "All invoices", seasons: "Seasons",
    exportExcel: "Export to Excel", noResults: "No SL payments match the current filters.",
    loading: "Loading…", loadError: "Could not load payments",
    edit: "Edit", delete: "Delete",
    deleteConfirmTitle: "Delete payment {id}?",
    deleteConfirmBody: "All allocations to invoices will be reversed and their balances recalculated.",
    deleteCancel: "Cancel", deleteConfirm: "Delete", deleting: "Deleting…",
    saveError: "Could not save changes", deleteError: "Could not delete payment",
    saved: "Payment updated", deleted: "Payment deleted",
    methodWire: "Wire", methodCheck: "Check", methodAch: "ACH", methodTransfer: "Transfer", methodCash: "Cash", methodOther: "Other",
    batchDepositTitle: "Batch Deposit", editPaymentTitle: "Edit Payment",
    depositDate: "Deposit Date", depositMethod: "Method", depositReference: "Reference / Wire #",
    refColumn: "Reference", dueDate: "Due Date", dueAmount: "Due Amount", depositAmount: "Deposit Amount",
    totalDeposit: "Total Deposit", notes: "Notes", cancel: "Cancel", recordDeposits: "Save Changes",
    noOpenInvoices: "No invoices to show.", overApplyInv: "Deposit cannot exceed Due Amount",
    concept: "Concept", conceptDeposit: "Deposit", conceptAdvance: "Advance",
    preview: "Preview",
  },
  es: {
    title: "Pagos SL", subtitle: "Pagos registrados desde dentro del módulo SweetLeaf.",
    payId: "PAY-ID", paymentDate: "Fecha de Pago", client: "Cliente", invoicesApplied: "Factura(s) Aplicadas",
    amount: "Monto", method: "Método", reference: "Referencia", status: "Estado", actions: "Acciones",
    statusApplied: "Aplicado", statusPending: "Pendiente de Aplicar",
    cardTotalDeposits: "Total Depósitos (temporada activa)", cardThisMonth: "Depósitos Este Mes", cardPendingAlloc: "Pendiente de Aplicar",
    search: "Buscar PAY-ID, referencia, factura…", from: "Desde", to: "Hasta",
    filterInvoice: "Factura", allInvoices: "Todas las facturas", seasons: "Temporadas",
    exportExcel: "Exportar a Excel", noResults: "Ningún pago SL coincide con los filtros actuales.",
    loading: "Cargando…", loadError: "No se pudieron cargar los pagos",
    edit: "Editar", delete: "Eliminar",
    deleteConfirmTitle: "¿Eliminar pago {id}?",
    deleteConfirmBody: "Todas las asignaciones a facturas se revertirán y los balances se recalcularán.",
    deleteCancel: "Cancelar", deleteConfirm: "Eliminar", deleting: "Eliminando…",
    saveError: "No se pudieron guardar los cambios", deleteError: "No se pudo eliminar el pago",
    saved: "Pago actualizado", deleted: "Pago eliminado",
    methodWire: "Transferencia", methodCheck: "Cheque", methodAch: "ACH", methodTransfer: "Transferencia", methodCash: "Efectivo", methodOther: "Otro",
    batchDepositTitle: "Depósito en Lote", editPaymentTitle: "Editar Pago",
    depositDate: "Fecha del Depósito", depositMethod: "Método", depositReference: "Referencia / # Transferencia",
    refColumn: "Referencia", dueDate: "Fecha de Pago", dueAmount: "Por Cobrar", depositAmount: "Monto del Depósito",
    totalDeposit: "Total del Depósito", notes: "Notas", cancel: "Cancelar", recordDeposits: "Guardar Cambios",
    noOpenInvoices: "No hay facturas que mostrar.", overApplyInv: "El depósito no puede exceder el saldo pendiente",
    concept: "Concepto", conceptDeposit: "Depósito", conceptAdvance: "Adelanto",
    preview: "Vista previa",
  },
};

function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch (e) { return fb; } }
function fmtMoney(n) { var v = parseFloat(n) || 0; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { if (!d) return "—"; var dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }); }

/* Same formula as SweetLeafPortal.js / InvoiceRegistry.js / paymentConfirmation.js
   — duplicated per this codebase's per-file convention (not imported;
   none of those modules export it). */
function calcInvoiceTotal(inv) {
  var total = 0;
  (inv.lines || []).forEach(function (l) {
    if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
      total += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    }
  });
  var tFees = 0;
  (inv.fees || []).forEach(function (fe) { tFees += parseFloat(fe.amount) || 0; });
  return total - tFees;
}

function paymentTotal(p) {
  return (p.allocations || []).reduce(function (s, a) { return s + (parseFloat(a.amount) || 0); }, 0);
}

/* Recomputes payments[]/amountPaid/status on every SL invoice touched by
   `paymentId`, given the NEW set of allocations for that payment (empty
   array = fully reversed, used by delete). Mirrors the dedupe-by-
   paymentId pattern SweetLeafPortal.js's handleBatchDepositConfirm
   already uses, so an edit/delete here behaves exactly like a fresh
   batch deposit would. Only touches invoices that are either in
   `newAllocations` or were previously allocated (`touchedInvoiceIds`) —
   an edit that removes an invoice from the allocation still needs its
   stale payments[] entry cleaned up. */
function buildInvoiceUpdates(paymentId, paymentDate, paymentMethod, paymentReference, newAllocations, touchedInvoiceIds, invoicesById) {
  var allocByInvId = {};
  (newAllocations || []).forEach(function (a) { allocByInvId[a.invoiceId] = a; });
  var out = [];
  touchedInvoiceIds.forEach(function (invId) {
    var inv = invoicesById[invId];
    if (!inv) return;
    var alloc = allocByInvId[invId];
    var existingPays = (inv.payments || []).filter(function (p) { return p.paymentId !== paymentId; });
    var newPayments = alloc
      ? existingPays.concat([{ paymentId: paymentId, date: paymentDate, amount: alloc.amount, method: paymentMethod, reference: paymentReference, concept: alloc.concept || "" }])
      : existingPays;
    var totalPaid = newPayments.reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
    var totalDue = calcInvoiceTotal(inv);
    var due = totalDue - totalPaid;
    var newStatus = inv.status;
    if (inv.status !== "draft" && due <= 0.01 && totalPaid > 0) newStatus = "paid";
    else if (newStatus === "paid" && due > 0.01) newStatus = "sent";
    out.push(Object.assign({}, inv, { payments: newPayments, amountPaid: +totalPaid.toFixed(2), status: newStatus }));
  });
  return out;
}

export default function SLPaymentsRegistry({ lang }) {
  var t = T[lang === "es" ? "es" : "en"];

  var [payments, setPayments] = useState([]);
  var [invoices, setInvoices] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [toast, setToast] = useState(null);

  var [search, setSearch] = useState("");
  var [dateFrom, setDateFrom] = useState("");
  var [dateTo, setDateTo] = useState("");
  var [filterInvoiceId, setFilterInvoiceId] = useState("");
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [sortCol, setSortCol] = useState("paymentDate");
  var [sortDir, setSortDir] = useState("desc");

  var [editingPayment, setEditingPayment] = useState(null);
  var [deletingPayment, setDeletingPayment] = useState(null);
  var [deleting, setDeleting] = useState(false);
  var [previewPayment, setPreviewPayment] = useState(null);

  var seasons = useMemo(function () { return loadLS("gfi-seasons", []); }, []);

  var reload = function () {
    setLoading(true); setError(null);
    Promise.all([
      loadFromSupabase("payments", { force: true }),
      loadFromSupabase("sweetleaf_invoices", { force: true }),
    ]).then(function (results) {
      var allPayments = results[0] || [];
      var slInvoices = results[1] || [];
      setPayments(allPayments.filter(function (p) { return p.source === "sl"; }));
      setInvoices(slInvoices);
    }).catch(function (e) {
      setError(t.loadError + ": " + (e && e.message ? e.message : String(e)));
    }).finally(function () {
      setLoading(false);
    });
  };
  useEffect(reload, []); // eslint-disable-line react-hooks/exhaustive-deps

  var invoicesById = useMemo(function () {
    var m = {};
    invoices.forEach(function (inv) { m[inv.id] = inv; });
    return m;
  }, [invoices]);

  var invoiceFilterOptions = useMemo(function () {
    return invoices
      .filter(function (inv) { return (payments || []).some(function (p) { return (p.allocations || []).some(function (a) { return a.invoiceId === inv.id; }); }); })
      .map(function (inv) { return { value: inv.id, label: (inv.invoiceNumber || inv.puNumber || inv.id) + " — " + (inv.clientName || "") }; })
      .sort(function (a, b) { return a.label.localeCompare(b.label); });
  }, [invoices, payments]);

  var seasonOptions = useMemo(function () { return buildSeasonFilterOptions(lang); }, [lang, seasons]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Resolves a payment's season from its first allocation's invoice
     date — a payment has no date-of-its-own-record season tag, so it
     borrows the invoice's (same convention as elsewhere: season is a
     property of when the underlying operation happened, not of the
     payment transaction date, which can lag). */
  function seasonIdForPayment(p) {
    var firstAlloc = (p.allocations || [])[0];
    var inv = firstAlloc ? invoicesById[firstAlloc.invoiceId] : null;
    if (!inv) return "";
    return resolveSeasonId(seasons, inv.date, inv.seasonId);
  }

  var filtered = useMemo(function () {
    var q = search.trim().toLowerCase();
    var seasonSet = filterSeasons.length ? new Set(filterSeasons) : null;
    return (payments || []).filter(function (p) {
      if (dateFrom && (p.paymentDate || "") < dateFrom) return false;
      if (dateTo && (p.paymentDate || "") > dateTo) return false;
      if (filterInvoiceId && !(p.allocations || []).some(function (a) { return a.invoiceId === filterInvoiceId; })) return false;
      if (seasonSet) {
        var sid = seasonIdForPayment(p);
        if (!sid || !seasonSet.has(sid)) return false;
      }
      if (q) {
        var invRefs = (p.allocations || []).map(function (a) { var inv = invoicesById[a.invoiceId]; return inv ? (inv.invoiceNumber || inv.puNumber || "") : ""; }).join(" ");
        var hay = ((p.id || "") + " " + (p.reference || "") + " " + (p.clientName || p.shipperName || "") + " " + invRefs).toLowerCase();
        if (hay.indexOf(q) < 0) return false;
      }
      return true;
    });
  }, [payments, search, dateFrom, dateTo, filterInvoiceId, filterSeasons, invoicesById, seasons]); // eslint-disable-line react-hooks/exhaustive-deps

  var sorted = useMemo(function () {
    var rows = filtered.slice();
    rows.sort(function (a, b) {
      var va, vb;
      if (sortCol === "amount") { va = paymentTotal(a); vb = paymentTotal(b); }
      else if (sortCol === "id") { va = a.id || ""; vb = b.id || ""; }
      else if (sortCol === "client") { va = a.clientName || a.shipperName || ""; vb = b.clientName || b.shipperName || ""; }
      else { va = a.paymentDate || ""; vb = b.paymentDate || ""; }
      if (typeof va === "string") { var c = va.localeCompare(vb); return sortDir === "asc" ? c : -c; }
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return rows;
  }, [filtered, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(function (d) { return d === "asc" ? "desc" : "asc"; });
    else { setSortCol(col); setSortDir("desc"); }
  }
  function arrow(col) { return sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"; }

  /* Cards */
  var cardTotalActiveSeason = useMemo(function () {
    var openIds = defaultOpenSeasonIds();
    var openSet = new Set(openIds);
    return (payments || []).reduce(function (s, p) {
      var sid = seasonIdForPayment(p);
      return (sid && openSet.has(sid)) ? s + paymentTotal(p) : s;
    }, 0);
  }, [payments, invoicesById, seasons]); // eslint-disable-line react-hooks/exhaustive-deps

  var cardThisMonth = useMemo(function () {
    var now = new Date();
    var ym = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
    return (payments || []).reduce(function (s, p) { return (p.paymentDate || "").slice(0, 7) === ym ? s + paymentTotal(p) : s; }, 0);
  }, [payments]);

  var cardPendingAlloc = useMemo(function () {
    return (payments || []).reduce(function (s, p) { return (p.allocations || []).length === 0 ? s + (parseFloat(p.amount) || 0) : s; }, 0);
  }, [payments]);

  function invoiceRefsForPayment(p) {
    return (p.allocations || []).map(function (a) {
      var inv = invoicesById[a.invoiceId];
      return inv ? (inv.invoiceNumber || inv.puNumber || a.invoiceId) : (a.puNumber || a.invoiceId);
    }).join(", ");
  }

  function openEdit(p) {
    var invIds = (p.allocations || []).map(function (a) { return a.invoiceId; });
    var invs = invIds.map(function (id) { return invoicesById[id]; }).filter(Boolean);
    setEditingPayment({ payment: p, invoices: invs });
  }

  function handleEditConfirm(payload) {
    var payment = editingPayment.payment;
    var touchedInvoiceIds = Array.from(new Set(
      (payment.allocations || []).map(function (a) { return a.invoiceId; })
        .concat(payload.allocations.map(function (a) { return a.invoiceId; }))
    ));
    var updatedRecord = Object.assign({}, payment, {
      paymentDate: payload.paymentDate,
      paymentMethod: payload.paymentMethod,
      reference: payload.reference,
      notes: payload.notes,
      allocations: payload.allocations,
    });
    var invoiceUpdates = buildInvoiceUpdates(payment.id, payload.paymentDate, payload.paymentMethod, payload.reference, payload.allocations, touchedInvoiceIds, invoicesById);
    Promise.all([
      saveToSupabase("payments", updatedRecord),
      invoiceUpdates.length > 0 ? saveManyToSupabase("sweetleaf_invoices", invoiceUpdates) : Promise.resolve([]),
    ]).then(function () {
      setEditingPayment(null);
      setToast(t.saved);
      reload();
    }).catch(function (e) {
      alert(t.saveError + ": " + (e && e.message ? e.message : String(e)));
    });
  }

  function confirmDelete() {
    if (!deletingPayment || deleting) return;
    setDeleting(true);
    var payment = deletingPayment;
    var touchedInvoiceIds = (payment.allocations || []).map(function (a) { return a.invoiceId; });
    var invoiceUpdates = buildInvoiceUpdates(payment.id, payment.paymentDate, payment.paymentMethod, payment.reference, [], touchedInvoiceIds, invoicesById);
    Promise.all([
      deleteFromSupabase("payments", payment.id),
      invoiceUpdates.length > 0 ? saveManyToSupabase("sweetleaf_invoices", invoiceUpdates) : Promise.resolve([]),
    ]).then(function () {
      setDeletingPayment(null);
      setToast(t.deleted);
      reload();
    }).catch(function (e) {
      alert(t.deleteError + ": " + (e && e.message ? e.message : String(e)));
    }).finally(function () {
      setDeleting(false);
    });
  }

  function handleExport() {
    downloadXlsx("SLPayments", "List", {
      Payments: sorted.map(function (p) {
        return {
          "PAY-ID": p.id, "Payment Date": p.paymentDate, Client: p.clientName || p.shipperName || "",
          "Invoice(s)": invoiceRefsForPayment(p), Amount: paymentTotal(p), Method: p.paymentMethod,
          Reference: p.reference, Status: (p.allocations || []).length > 0 ? t.statusApplied : t.statusPending,
        };
      }),
    });
  }

  var th = { padding: "9px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "2px solid var(--color-border)", cursor: "pointer", whiteSpace: "nowrap" };
  var td = { padding: "8px 8px", fontSize: 12, borderBottom: "1px solid var(--color-border)" };

  return (
    <div>
      {toast && (function () {
        setTimeout(function () { setToast(null); }, 3000);
        return <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "var(--color-accent)", color: "var(--color-accent-text)", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 999999, boxShadow: "0 8px 24px var(--color-shadow)" }}>{toast}</div>;
      })()}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t.title}</h2>
          <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: "4px 0 0" }}>{t.subtitle}</p>
        </div>
        <button onClick={handleExport} style={{ padding: "7px 14px", borderRadius: 7, border: "1px solid var(--color-border)", background: "var(--color-bg-card)", color: "var(--color-text)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t.exportExcel}</button>
      </div>

      {error && <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#DC2626", fontSize: 12, fontWeight: 600, marginBottom: 14 }}>{error}</div>}

      {/* Cards */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        {[[t.cardTotalDeposits, cardTotalActiveSeason], [t.cardThisMonth, cardThisMonth], [t.cardPendingAlloc, cardPendingAlloc]].map(function (pair, i) {
          return (
            <div key={i} style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: "12px 16px", minWidth: 180, flex: "1 1 180px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{pair[0]}</div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", color: "#059669" }}>{fmtMoney(pair[1])}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14, alignItems: "flex-end" }}>
        <input value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder={t.search} style={{ padding: "7px 10px", fontSize: 12, borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", color: "var(--color-text)", minWidth: 220 }} />
        <div>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 3 }}>{t.from}</div>
          <input type="date" value={dateFrom} onChange={function (e) { setDateFrom(e.target.value); }} style={{ padding: "6px 8px", fontSize: 12, borderRadius: 5, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", color: "var(--color-text)" }} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 3 }}>{t.to}</div>
          <input type="date" value={dateTo} onChange={function (e) { setDateTo(e.target.value); }} style={{ padding: "6px 8px", fontSize: 12, borderRadius: 5, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", color: "var(--color-text)" }} />
        </div>
        <div style={{ minWidth: 220 }}>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 3 }}>{t.filterInvoice}</div>
          <SearchSelect value={filterInvoiceId} onChange={setFilterInvoiceId} placeholder={t.allInvoices} options={invoiceFilterOptions} />
        </div>
        <div style={{ minWidth: 200 }}>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 3 }}>{t.seasons}</div>
          <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={seasonOptions} />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>{t.loading}</div>
      ) : (
        <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th} onClick={function () { toggleSort("id"); }}>{t.payId}{arrow("id")}</th>
                <th style={th} onClick={function () { toggleSort("paymentDate"); }}>{t.paymentDate}{arrow("paymentDate")}</th>
                <th style={th} onClick={function () { toggleSort("client"); }}>{t.client}{arrow("client")}</th>
                <th style={th}>{t.invoicesApplied}</th>
                <th style={{ ...th, textAlign: "right" }} onClick={function () { toggleSort("amount"); }}>{t.amount}{arrow("amount")}</th>
                <th style={th}>{t.method}</th>
                <th style={th}>{t.reference}</th>
                <th style={{ ...th, textAlign: "center", cursor: "default" }}>{t.status}</th>
                <th style={{ ...th, textAlign: "center", cursor: "default" }}>{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(function (p) {
                var applied = (p.allocations || []).length > 0;
                return (
                  <tr key={p.id} style={{ cursor: "pointer" }} onClick={function () { setPreviewPayment(p); }}>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#2A7A6F" }}>{p.id}</td>
                    <td style={td}>{fmtDate(p.paymentDate)}</td>
                    <td style={td}>{p.clientName || p.shipperName || "—"}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{invoiceRefsForPayment(p) || "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{fmtMoney(paymentTotal(p))}</td>
                    <td style={td}>{t["method" + (p.paymentMethod ? p.paymentMethod.charAt(0).toUpperCase() + p.paymentMethod.slice(1) : "")] || p.paymentMethod || "—"}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{p.reference || "—"}</td>
                    <td style={{ ...td, textAlign: "center" }}>
                      <span style={{ padding: "3px 9px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: applied ? "rgba(16,185,129,0.12)" : "rgba(217,119,6,0.12)", color: applied ? "#059669" : "#D97706", border: "1px solid " + (applied ? "rgba(16,185,129,0.3)" : "rgba(217,119,6,0.3)") }}>{applied ? t.statusApplied : t.statusPending}</span>
                    </td>
                    <td style={{ ...td, textAlign: "center" }} onClick={function (e) { e.stopPropagation(); }}>
                      <button onClick={function () { openEdit(p); }} title={t.edit} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--color-text-muted)", marginRight: 8 }}>✎</button>
                      <button onClick={function () { setDeletingPayment(p); }} title={t.delete} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#DC2626" }}>🗑</button>
                    </td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, textAlign: "center", fontSize: 12, color: "var(--color-text-muted)" }}>{t.noResults}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editingPayment && (
        <BatchDepositModal
          invoices={editingPayment.invoices}
          existingPayment={editingPayment.payment}
          t={t}
          lang={lang}
          onConfirm={handleEditConfirm}
          onCancel={function () { setEditingPayment(null); }}
        />
      )}

      {deletingPayment && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 20002, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={function () { if (!deleting) setDeletingPayment(null); }}>
          <div style={{ background: "var(--color-bg-card-solid)", borderRadius: 10, padding: 22, minWidth: 360, maxWidth: 460, boxShadow: "0 24px 64px rgba(0,0,0,0.3)", border: "1px solid var(--color-border)" }} onClick={function (e) { e.stopPropagation(); }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t.deleteConfirmTitle.replace("{id}", deletingPayment.id)}</div>
            <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 18 }}>{t.deleteConfirmBody}</div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={function () { setDeletingPayment(null); }} disabled={deleting} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: deleting ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 500 }}>{t.deleteCancel}</button>
              <button onClick={confirmDelete} disabled={deleting} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: deleting ? "var(--color-border)" : "#DC2626", color: "#fff", cursor: deleting ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>{deleting ? t.deleting : t.deleteConfirm}</button>
            </div>
          </div>
        </div>
      )}

      {previewPayment && (function () {
        var pr = partyAndRowsForInvoiceDeposit(previewPayment, { invoices: invoices, brand: "sl" });
        return <PaymentConfirmationPreview payment={previewPayment} party={pr.party} rows={pr.rows} lang={lang} onClose={function () { setPreviewPayment(null); }} />;
      })()}
    </div>
  );
}
